import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import MenuImport from "@/components/MenuImport";
import PriceImpact from "@/components/PriceImpact";
import { Plus, ChefHat, Search, Trash2, Copy, ChevronRight, GripVertical, AlertTriangle, ShieldQuestion, ShieldCheck, X } from "lucide-react";

const money = (v, dp = 2) => (v == null ? "—" : `£${Number(v).toFixed(dp)}`);
const UNITS = ["kg", "g", "mg", "litre", "ml", "each"];

function ProvBadge({ source }) {
  const map = {
    supplier_master: ["Supplier price", "bg-slate-100 text-slate-600"],
    account: ["Account price", "bg-blue-50 text-blue-600"],
    local_override: ["Local override", "bg-brand-light text-brand-dark"],
  };
  const [label, cls] = map[source] || [null, ""];
  if (!label) return null;
  return <span data-testid={`prov-${source}`} className={`inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${cls}`}>{label}</span>;
}

function AllergenPill({ status }) {
  if (status === "confirmed") return <span data-testid="recipe-allergen-status" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-brand-light text-brand-dark"><ShieldCheck size={11} /> Allergens confirmed</span>;
  return <span data-testid="recipe-allergen-status" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-600"><ShieldQuestion size={11} /> Allergen information incomplete</span>;
}

export default function Recipes() {
  const { has, moduleEnabled } = useAuth();
  const canManage = has("manage_recipes");
  const [data, setData] = useState({ recipes: [], total: 0 });
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [archived, setArchived] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editor, setEditor] = useState(false); // false | {} (new) | recipe
  const [menuImport, setMenuImport] = useState(false);
  const [impact, setImpact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (search.trim()) params.set("search", search.trim());
      if (category !== "all") params.set("category", category);
      if (archived) params.set("archived", "true");
      const { data } = await api.get(`/recipes?${params.toString()}`);
      setData(data);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [search, category, archived]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/recipes/categories").then((r) => setCategories(r.data)).catch(() => {}); }, [editor]);

  const openRecipe = async (rid) => {
    try { const { data } = await api.get(`/recipes/${rid}`); setEditor(data); }
    catch { toast.error("Couldn't open recipe"); }
  };
  const duplicate = async (rid, e) => {
    e.stopPropagation();
    try { const { data } = await api.post(`/recipes/${rid}/duplicate`, {}); toast.success("Recipe duplicated"); load(); openRecipe(data.recipe_id); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  const archiveRecipe = async (rid, e) => {
    e.stopPropagation();
    try { await api.delete(`/recipes/${rid}`); toast.success("Recipe archived"); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  const restoreRecipe = async (rid, e) => {
    e.stopPropagation();
    try { await api.post(`/recipes/${rid}/restore`, {}); toast.success("Recipe restored"); load(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <PageHeader title="Recipes" subtitle="Costed recipes built from your live product catalogue." testid="recipes-header"
        action={<div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-xl" data-testid="price-impact-btn" onClick={() => setImpact(true)}>Price impact</Button>
          {canManage && moduleEnabled("ai_menu_recipe") && <Button variant="outline" className="rounded-xl" data-testid="menu-import-btn" onClick={() => setMenuImport(true)}>Import menu</Button>}
          {canManage && <Button data-testid="add-recipe-btn" onClick={() => setEditor({})} className="bg-brand hover:bg-brand-hover rounded-xl"><Plus size={18} className="mr-1" /> New recipe</Button>}</div>} />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search recipes…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="recipe-search" className="pl-9" />
        </div>
        <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="recipe-category-filter" className="w-44"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        <Button variant={archived ? "default" : "outline"} className="rounded-xl" data-testid="recipe-archived-toggle" onClick={() => setArchived((a) => !a)}>{archived ? "Archived" : "Active"}</Button>
      </div>

      {loading && data.recipes.length === 0 ? <div className="text-sm text-slate-400 p-4">Loading…</div> :
        error ? <div className="cc-card p-6 text-center text-sm"><p className="text-red-500 mb-2">Couldn't load recipes.</p><Button variant="outline" size="sm" onClick={load}>Retry</Button></div> :
          data.recipes.length === 0 ? <EmptyState icon={ChefHat} title="No recipes yet." message={canManage ? "Create your first costed recipe." : "No recipes to show."} /> :
            <div className="space-y-2">
              {data.recipes.map((r) => (
                <div key={r.recipe_id} className="cc-list-row" data-testid={`recipe-${r.recipe_id}`} onClick={() => openRecipe(r.recipe_id)}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-11 w-11 rounded-xl bg-brand-light flex items-center justify-center shrink-0"><ChefHat className="text-brand-dark" size={20} /></div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate flex items-center gap-2">{r.name}
                        {r.review_status === "draft" && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700" data-testid={`recipe-draft-${r.recipe_id}`}>Draft · from menu import</span>}
                        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{(r.site_ids || []).length ? `${r.site_ids.length} site(s)` : "org-wide"}</span></div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                        {r.category && <span>{r.category}</span>}
                        {r.cost_summary?.complete
                          ? <><span>Cost {money(r.cost_summary?.total_ingredient_cost)}</span><span>· {money(r.cost_summary?.cost_per_portion)}/portion</span></>
                          : <span className="text-amber-600 font-semibold">Costing incomplete</span>}
                        {r.cost_summary?.complete && r.cost_summary?.gross_profit_percent != null && <span>· GP {r.cost_summary.gross_profit_percent}%</span>}
                        <AllergenPill status={r.cost_summary?.allergen_status} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canManage && !archived && <button data-testid={`duplicate-recipe-${r.recipe_id}`} title="Duplicate" onClick={(e) => duplicate(r.recipe_id, e)} className="p-2 text-slate-400 hover:text-brand"><Copy size={16} /></button>}
                    {canManage && !archived && <button data-testid={`archive-recipe-${r.recipe_id}`} title="Archive" onClick={(e) => archiveRecipe(r.recipe_id, e)} className="p-2 text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>}
                    {canManage && archived && <button data-testid={`restore-recipe-${r.recipe_id}`} title="Restore" onClick={(e) => restoreRecipe(r.recipe_id, e)} className="text-xs font-semibold text-brand-dark px-2">Restore</button>}
                    <ChevronRight size={16} className="text-slate-300" />
                  </div>
                </div>
              ))}
            </div>}

      {menuImport && <MenuImport onClose={() => setMenuImport(false)} onDone={load} />}
      {impact && <PriceImpact onClose={() => setImpact(false)} />}
      {editor !== false && <RecipeEditor recipe={editor.recipe_id ? editor : null} canManage={canManage} onClose={() => setEditor(false)} onSaved={() => { setEditor(false); load(); }} />}
    </div>
  );
}

function ProductPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/products?search=${encodeURIComponent(q.trim())}&limit=20`); setResults(data); setOpen(true); } catch {}
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input placeholder="Search catalogue to add an ingredient…" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)} data-testid="ingredient-product-search" className="pl-9" />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="ingredient-results">
          {results.map((p) => (
            <button key={p.product_id} type="button" data-testid={`pick-product-${p.product_id}`} onClick={() => { onPick(p); setQ(""); setResults([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm flex justify-between items-center">
              <span>{p.name} {p.pack_size && <span className="text-xs text-slate-400">· {p.pack_size}</span>}</span>
              <span className="text-slate-500 text-xs">{p.price != null ? money(p.price) : "no price"}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RecipeEditor({ recipe, canManage, onClose, onSaved }) {
  const editing = !!recipe;
  const ro = !canManage;
  const [f, setF] = useState(recipe ? {
    name: recipe.name || "", description: recipe.description || "", category: recipe.category || "", code: recipe.code || "",
    yield_quantity: recipe.yield_quantity ?? "", yield_unit: recipe.yield_unit || "", portion_quantity: recipe.portion_quantity ?? "",
    number_of_portions: recipe.number_of_portions ?? "", prep_time_minutes: recipe.prep_time_minutes ?? "", cook_time_minutes: recipe.cook_time_minutes ?? "",
    method: recipe.method || "", chef_notes: recipe.chef_notes || "", site_ids: recipe.site_ids || [],
    target_food_cost_percent: recipe.target_food_cost_percent ?? 30, proposed_selling_price: recipe.proposed_selling_price ?? "", vat_rate: recipe.vat_rate ?? 20,
  } : { name: "", description: "", category: "", code: "", yield_quantity: "", yield_unit: "", portion_quantity: "", number_of_portions: "", prep_time_minutes: "", cook_time_minutes: "", method: "", chef_notes: "", site_ids: [], target_food_cost_percent: 30, proposed_selling_price: "", vat_rate: 20 });
  const [ingredients, setIngredients] = useState(recipe?.ingredients || []);
  const [venues, setVenues] = useState([]);
  const [costing, setCosting] = useState(recipe?.costing || null);
  const [saving, setSaving] = useState(false);
  const debounce = useRef(null);

  useEffect(() => { api.get("/venues").then((r) => setVenues(r.data || [])).catch(() => setVenues([])); }, []);

  const preview = useCallback(async (ings, form) => {
    try {
      const { data } = await api.post("/recipes/cost-preview", {
        ingredients: ings.map((i) => ({ product_id: i.product_id, quantity: i.quantity, unit: i.unit, wastage_percent: i.wastage_percent })),
        number_of_portions: form.number_of_portions, proposed_selling_price: form.proposed_selling_price, vat_rate: form.vat_rate, target_food_cost_percent: form.target_food_cost_percent,
      });
      setCosting(data);
    } catch {}
  }, []);
  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => preview(ingredients, f), 350);
    return () => clearTimeout(debounce.current);
  }, [ingredients, f.number_of_portions, f.proposed_selling_price, f.vat_rate, f.target_food_cost_percent, preview]);

  const addProduct = (p) => setIngredients((list) => [...list, { ingredient_id: `tmp_${Date.now()}`, product_id: p.product_id, product_name: p.name, quantity: 1, unit: (p.purchase_unit && UNITS.includes(p.purchase_unit)) ? p.purchase_unit : "g", wastage_percent: 0, position: list.length }]);
  const updIng = (idx, k, v) => setIngredients((list) => list.map((it, i) => i === idx ? { ...it, [k]: v } : it));
  const removeIng = (idx) => setIngredients((list) => list.filter((_, i) => i !== idx).map((it, i) => ({ ...it, position: i })));
  const move = (idx, dir) => setIngredients((list) => { const n = [...list]; const j = idx + dir; if (j < 0 || j >= n.length) return list; [n[idx], n[j]] = [n[j], n[idx]]; return n.map((it, i) => ({ ...it, position: i })); });
  const toggleSite = (id) => setF((s) => ({ ...s, site_ids: s.site_ids.includes(id) ? s.site_ids.filter((x) => x !== id) : [...s.site_ids, id] }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Recipe name is required");
    setSaving(true);
    const payload = {
      ...f,
      yield_quantity: f.yield_quantity === "" ? null : Number(f.yield_quantity),
      portion_quantity: f.portion_quantity === "" ? null : Number(f.portion_quantity),
      number_of_portions: f.number_of_portions === "" ? null : Number(f.number_of_portions),
      prep_time_minutes: f.prep_time_minutes === "" ? null : Number(f.prep_time_minutes),
      cook_time_minutes: f.cook_time_minutes === "" ? null : Number(f.cook_time_minutes),
      target_food_cost_percent: f.target_food_cost_percent === "" ? null : Number(f.target_food_cost_percent),
      proposed_selling_price: f.proposed_selling_price === "" ? null : Number(f.proposed_selling_price),
      vat_rate: f.vat_rate === "" ? 20 : Number(f.vat_rate),
      ingredients: ingredients.map((i, idx) => ({ product_id: i.product_id, quantity: i.quantity === "" ? null : Number(i.quantity), unit: i.unit, prep_note: i.prep_note || "", wastage_percent: i.wastage_percent === "" ? 0 : Number(i.wastage_percent), position: idx })),
    };
    try {
      if (editing) await api.put(`/recipes/${recipe.recipe_id}`, payload);
      else await api.post("/recipes", payload);
      toast.success(editing ? "Recipe saved" : "Recipe created"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const c = costing || {};
  const com = c.commercial || {};

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="recipe-editor" aria-describedby="recipe-editor-desc" className="max-w-3xl">
        <DialogHeader><DialogTitle>{editing ? "Edit recipe" : "New recipe"}</DialogTitle></DialogHeader>
        <p id="recipe-editor-desc" className="sr-only">Create or edit a recipe with live ingredient costing.</p>
        <fieldset disabled={ro} className="grid md:grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto pr-1 border-0 p-0 m-0 min-w-0">
          {/* Left: details + ingredients + method */}
          <div className="md:col-span-2 space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Fld label="Recipe name" v={f.name} set={(v) => setF({ ...f, name: v })} testid="recipe-name" /></div>
              <Fld label="Code" v={f.code} set={(v) => setF({ ...f, code: v })} testid="recipe-code" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Fld label="Category" v={f.category} set={(v) => setF({ ...f, category: v })} testid="recipe-category" />
              <Fld label="Prep (min)" type="number" v={f.prep_time_minutes} set={(v) => setF({ ...f, prep_time_minutes: v })} testid="recipe-prep" />
              <Fld label="Cook (min)" type="number" v={f.cook_time_minutes} set={(v) => setF({ ...f, cook_time_minutes: v })} testid="recipe-cook" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Fld label="Yield qty" type="number" v={f.yield_quantity} set={(v) => setF({ ...f, yield_quantity: v })} testid="recipe-yield-qty" />
              <Fld label="Yield unit" v={f.yield_unit} set={(v) => setF({ ...f, yield_unit: v })} testid="recipe-yield-unit" />
              <Fld label="Portions" type="number" v={f.number_of_portions} set={(v) => setF({ ...f, number_of_portions: v })} testid="recipe-portions" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Ingredients</Label>
              {canManage && <ProductPicker onPick={addProduct} />}
              <div className="space-y-1.5 mt-2">
                {ingredients.length === 0 && <p className="text-xs text-slate-400">No ingredients yet — search the catalogue above.</p>}
                {ingredients.map((i, idx) => {
                  const cr = costing?.ingredients?.[idx];
                  const flagged = cr && cr.status !== "ok";
                  return (
                    <div key={i.ingredient_id || idx} className={`border rounded-lg p-2 ${flagged ? "border-amber-300 bg-amber-50/50" : ""}`} data-testid={`ingredient-row-${idx}`}>
                      <div className="flex items-center gap-2">
                        {canManage && <div className="flex flex-col text-slate-300"><button type="button" onClick={() => move(idx, -1)} className="hover:text-slate-500 leading-none">▲</button><button type="button" onClick={() => move(idx, 1)} className="hover:text-slate-500 leading-none">▼</button></div>}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-700 truncate">{i.product_name || cr?.product_name}</div>
                          <div className="flex items-center gap-1.5 mt-1">
                            <Input type="number" value={i.quantity} onChange={(e) => updIng(idx, "quantity", e.target.value)} data-testid={`ingredient-qty-${idx}`} className="h-8 w-20" disabled={!canManage} />
                            <Select value={i.unit} onValueChange={(v) => updIng(idx, "unit", v)}><SelectTrigger data-testid={`ingredient-unit-${idx}`} className="h-8 w-24"><SelectValue /></SelectTrigger><SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select>
                            <Input type="number" value={i.wastage_percent} onChange={(e) => updIng(idx, "wastage_percent", e.target.value)} data-testid={`ingredient-wastage-${idx}`} className="h-8 w-16" placeholder="% waste" disabled={!canManage} title="Wastage %" />
                            <span className="ml-auto text-sm font-semibold text-slate-700" data-testid={`ingredient-cost-${idx}`}>{cr?.line_cost != null ? money(cr.line_cost, 3) : "—"}</span>
                            {canManage && <button type="button" onClick={() => removeIng(idx)} data-testid={`remove-ingredient-${idx}`} className="text-slate-300 hover:text-red-500"><X size={15} /></button>}
                          </div>
                          {cr?.price_source && cr.status === "ok" && <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1.5"><ProvBadge source={cr.price_source.provenance} /> {cr.price_source.supplier_name} · {money(cr.price_source.price)}{cr.price_source.pack_size ? `/${cr.price_source.pack_size}` : ""}{cr.price_source.is_fallback && <span className="text-amber-600 font-semibold"> · fallback (not preferred)</span>}</div>}
                          {flagged && <div className="text-[10px] text-amber-700 font-semibold mt-0.5 flex items-center gap-1" data-testid={`ingredient-flag-${idx}`}><AlertTriangle size={11} /> {cr.message}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div><Label className="text-xs font-semibold text-slate-600">Method</Label><Textarea value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="recipe-method" className="mt-1.5" rows={4} /></div>
            <div><Label className="text-xs font-semibold text-slate-600">Chef notes</Label><Textarea value={f.chef_notes} onChange={(e) => setF({ ...f, chef_notes: e.target.value })} data-testid="recipe-notes" className="mt-1.5" rows={2} /></div>
            {venues.length > 0 && (
              <div>
                <Label className="text-xs font-semibold text-slate-600">Sites</Label>
                <p className="text-[11px] text-slate-400 mb-1">Leave all unticked for organisation-wide.</p>
                <div className="border rounded-lg p-2 space-y-1 max-h-24 overflow-y-auto">
                  {venues.map((v) => <label key={v.venue_id} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid={`recipe-site-${v.venue_id}`}><Checkbox checked={f.site_ids.includes(v.venue_id)} onCheckedChange={() => toggleSite(v.venue_id)} className="h-4 w-4" /> {v.name}</label>)}
                </div>
              </div>
            )}
          </div>

          {/* Right: live costing */}
          <div className="space-y-3">
            <div className="cc-card p-4 sticky top-0" data-testid="recipe-cost-summary">
              <div className="text-xs font-bold uppercase text-slate-400 mb-2">Live costing</div>
              <Row k="Recipe cost" v={c.complete ? money(c.total_ingredient_cost) : (c.total_ingredient_cost ? `${money(c.total_ingredient_cost)} (partial)` : "—")} testid="summary-total" />
              <Row k="Cost / portion" v={c.complete ? money(c.cost_per_portion) : "—"} testid="summary-per-portion" strong />
              {!c.complete && <div className="text-[11px] text-amber-700 font-semibold my-2 flex items-center gap-1" data-testid="summary-incomplete"><AlertTriangle size={12} /> Costing incomplete — pricing/units need review</div>}
              {(c.missing_pricing?.length > 0) && <div className="text-[11px] text-slate-500">Missing price: {c.missing_pricing.join(", ")}</div>}
              {(c.requires_unit_review?.length > 0) && <div className="text-[11px] text-slate-500">Unit review: {c.requires_unit_review.join(", ")}</div>}
              <div className="border-t my-3" />
              <div className="text-xs font-bold uppercase text-slate-400 mb-2">Commercial (GP)</div>
              <div className="grid grid-cols-2 gap-2">
                <Fld label="Target food %" type="number" v={f.target_food_cost_percent} set={(v) => setF({ ...f, target_food_cost_percent: v })} testid="recipe-target-fc" />
                <div><Label className="text-xs font-semibold text-slate-600">VAT %</Label>
                  <Select value={String(f.vat_rate)} onValueChange={(v) => setF({ ...f, vat_rate: Number(v) })}><SelectTrigger data-testid="recipe-vat" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="20">20%</SelectItem><SelectItem value="0">0% (zero-rated)</SelectItem><SelectItem value="5">5%</SelectItem></SelectContent></Select></div>
              </div>
              <div className="mt-2"><Fld label="Menu price (inc VAT)" type="number" v={f.proposed_selling_price} set={(v) => setF({ ...f, proposed_selling_price: v })} testid="recipe-selling-price" /></div>
              <div className="mt-3 space-y-1">
                <Row k="Selling ex VAT" v={money(com.selling_price_ex_vat)} testid="summary-ex-vat" />
                <Row k="Gross profit" v={money(com.gross_profit)} testid="summary-gp" />
                <Row k="GP %" v={com.gross_profit_percent != null ? `${com.gross_profit_percent}%` : "—"} testid="summary-gp-pct" strong />
                <Row k="Food cost %" v={com.food_cost_percent != null ? `${com.food_cost_percent}%` : "—"} testid="summary-fc-pct" />
                {com.suggested_selling_price_inc_vat != null && <Row k="Suggested price" v={money(com.suggested_selling_price_inc_vat)} testid="summary-suggested" />}
              </div>
              <div className="border-t my-3" />
              <div className="text-xs font-bold uppercase text-slate-400 mb-1">Allergens</div>
              <AllergenPill status={c.allergens?.status} />
              {c.allergens?.allergens?.length > 0 && <div className="text-[11px] text-slate-600 mt-1 capitalize">Contains: {c.allergens.allergens.join(", ")}</div>}
              {c.allergens?.incomplete && <div className="text-[11px] text-amber-700 mt-1" data-testid="allergen-incomplete">Review: {c.allergens.unknown_ingredients.join(", ")}</div>}
            </div>
          </div>
        </fieldset>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canManage && <Button onClick={save} disabled={saving} data-testid="save-recipe" className="bg-brand hover:bg-brand-hover">{saving ? "Saving…" : editing ? "Save recipe" : "Create recipe"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v, strong, testid }) {
  return <div className="flex justify-between items-baseline text-sm"><span className="text-slate-500">{k}</span><span className={strong ? "font-display font-bold text-slate-800" : "text-slate-700"} data-testid={testid}>{v}</span></div>;
}
function Fld({ label, v, set, type = "text", testid }) {
  return <div><Label className="text-xs font-semibold text-slate-600">{label}</Label><Input type={type} value={v} onChange={(e) => set(e.target.value)} data-testid={testid} className="mt-1.5" /></div>;
}
